/**
 * Slovak business identifiers, and the check digits they carry.
 *
 * Lifted out of `manager-inventory`'s supplier editor when `manager-invoicing` needed
 * the same IBAN check for the account auto-payments would draw from. §5 forbids one
 * module importing another, and an IBAN checksum is not a supplier concern — it is
 * arithmetic about a bank account, which is exactly what `@agos/shared` is for.
 *
 * The algorithms are verified against real records rather than remembered: the two
 * fully-filled suppliers in the restored data, Bidfood `34152199` /
 * `SK48 7500 0080 1006 1102 2233` and LUNYS `36472549` / `SK74 0200 0000 0017 7905 4954`.
 *
 * Everything here **reports**; nothing refuses. A foreign supplier has no Slovak IČO
 * and a record legitimately starts empty — see FB-080 for why that matters.
 */

/** Local: `phone.ts` exports a `digitsOf` of its own for a different job. */
function onlyDigits(value: string | null | undefined): string {
  return (value ?? '').replace(/\D/g, '')
}

/**
 * The Slovak (and Czech) IČO check digit.
 *
 * Eight digits: the first seven weighted 8…2, summed, taken mod 11, and the remainder
 * mapped to the eighth. Confirmed against both real IČOs above.
 */
export function isValidIco(value: string | null | undefined): boolean {
  const digits = onlyDigits(value)
  if (digits.length !== 8) return false

  let sum = 0
  for (let position = 0; position < 7; position += 1) {
    sum += Number(digits[position]) * (8 - position)
  }

  const remainder = sum % 11
  const expected = remainder === 0 ? 1 : remainder === 1 ? 0 : 11 - remainder
  return Number(digits[7]) === expected
}

/** A Slovak DIČ is ten digits and carries no check of its own. */
export function isValidDic(value: string | null | undefined): boolean {
  return onlyDigits(value).length === 10
}

/**
 * An IČ DPH is the country code followed by the DIČ.
 *
 * Both filled suppliers in the data follow that exactly — `SK2020168359` beside
 * `2020168359`. So a pair that disagrees is a typo in one of the two, and which one is
 * not knowable from here.
 */
export function icDphMatchesDic(icDph: string | null | undefined, dic: string | null | undefined): boolean {
  const vat = (icDph ?? '').replace(/\s/g, '').toUpperCase()
  const taxId = onlyDigits(dic)
  if (vat === '' || taxId === '') return true
  return vat === `SK${taxId}`
}

/** `SK` plus ten digits. */
export function isValidIcDph(value: string | null | undefined): boolean {
  return /^SK\d{10}$/.test((value ?? '').replace(/\s/g, '').toUpperCase())
}

/**
 * The ISO 13616 mod-97 check every IBAN carries.
 *
 * The first four characters move to the end, letters become numbers (`A` = 10 … `Z` =
 * 35), and the result read as one integer must be 1 mod 97. Computed digit by digit
 * because a Slovak IBAN is 24 characters and the integer would not survive a `Number`.
 */
export function isValidIban(value: string | null | undefined): boolean {
  const iban = (value ?? '').replace(/\s/g, '').toUpperCase()
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(iban)) return false

  const rearranged = `${iban.slice(4)}${iban.slice(0, 4)}`
  let remainder = 0
  for (const character of rearranged) {
    const value_ = /\d/.test(character)
      ? character
      : String(character.charCodeAt(0) - 'A'.charCodeAt(0) + 10)
    for (const digit of value_) {
      remainder = (remainder * 10 + Number(digit)) % 97
    }
  }
  return remainder === 1
}

/** A Slovak IBAN is exactly 24 characters. */
export function isSlovakIban(value: string | null | undefined): boolean {
  const iban = (value ?? '').replace(/\s/g, '').toUpperCase()
  return iban.startsWith('SK') && iban.length === 24
}

/** What an IČ DPH would be, given a DIČ — the pair a mismatch is reported against. */
export function expectedIcDph(dic: string | null | undefined): string {
  return `SK${onlyDigits(dic)}`
}

/** `SK48 7500 0080 1006 1102 2233` — grouped in fours, the way a bank prints it. */
export function formatIban(value: string | null | undefined): string {
  const iban = (value ?? '').replace(/\s/g, '').toUpperCase()
  return iban.replace(/(.{4})/g, '$1 ').trim()
}
